import { Icon } from '@equinor/eds-core-react';
import {
  chevron_down as DOWN,
  chevron_right as RIGHT,
} from '@equinor/eds-icons';
import { useEffect, useRef, useState } from 'react';
import * as Styled from './ExpandableListCell.styled';

type ExpandableListCellProps = {
  values: string[];
};

export const isDisplayValue = (value: string) => {
  const normalizedValue = value.trim().toLowerCase();
  return (
    normalizedValue !== '' &&
    normalizedValue !== 'undefined' &&
    normalizedValue !== 'null'
  );
};

export const ExpandableListCell = ({ values }: ExpandableListCellProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const textRef = useRef<HTMLSpanElement>(null);
  const displayValues = values
    .map((cellValue) => cellValue.trim())
    .filter(isDisplayValue);
  const cellText = displayValues.join(', ');
  const shouldShowExpander = canExpand || displayValues.length > 1;

  useEffect(() => {
    const textElement = textRef.current;
    if (!textElement || isExpanded) return;

    if (displayValues.length > 1) return;

    const updateCanExpand = () => {
      setCanExpand(textElement.scrollWidth > textElement.clientWidth + 1);
    };

    updateCanExpand();

    if (typeof ResizeObserver === 'undefined') return;

    const resizeObserver = new ResizeObserver(updateCanExpand);
    resizeObserver.observe(textElement);

    if (textElement.parentElement) {
      resizeObserver.observe(textElement.parentElement);
    }

    return () => resizeObserver.disconnect();
  }, [cellText, displayValues.length, isExpanded]);

  if (!cellText) return <>-</>;

  if (!shouldShowExpander) {
    return (
      <Styled.StaticCellText ref={textRef}>{cellText}</Styled.StaticCellText>
    );
  }

  return (
    <Styled.ExpandableCellButton
      aria-expanded={isExpanded}
      aria-label={isExpanded ? 'Collapse cell text' : 'Expand cell text'}
      onClick={() => setIsExpanded((previous) => !previous)}
      type="button"
    >
      <Styled.ExpandableCellText ref={textRef} $isExpanded={isExpanded}>
        {cellText}
      </Styled.ExpandableCellText>
      <Styled.ExpandableCellIcon>
        <Icon data={isExpanded ? DOWN : RIGHT} size={16} />
      </Styled.ExpandableCellIcon>
    </Styled.ExpandableCellButton>
  );
};
